import { Card, CardContent, MetricInfoPopover } from '@/components/ui';
import type { ITeamQualityRow } from '@skystone/shared';
import { teamsForCoverage } from './team-row-formatter';

interface CoverageByTeamPanelProps {
  teams: ReadonlyArray<ITeamQualityRow>;
}

/**
 * "Coverage % by Team" panel — left half of the team-breakouts row at `lg+`.
 *
 * Excludes teams whose every in-scope repo has null coverage (per spec edge
 * case). Sort: weightedCoverage desc, alphabetical tie-break. The repo-count
 * cell drops at `base/sm` to keep the table readable on narrow widths.
 */
export function CoverageByTeamPanel({ teams }: Readonly<CoverageByTeamPanelProps>) {
  const rows = teamsForCoverage(teams);

  return (
    <Card className="shadow-none" data-testid="cq-coverage-by-team-panel">
      <CardContent className="p-4">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-semibold text-foreground">Coverage % by Team</h3>
          <MetricInfoPopover metricKey="cq_coverage" />
        </div>
        <p className="mt-1 text-xs italic text-muted-foreground">
          Average across each team's Sonar-onboarded repos.
        </p>
        {rows.length === 0 ? (
          <p
            className="mt-4 text-xs text-muted-foreground"
            data-testid="cq-coverage-by-team-empty"
          >
            No teams with coverage data in this scope yet.
          </p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="pb-2 pr-3 text-xs font-medium text-muted-foreground">Team</th>
                <th className="pb-2 pr-3 text-xs font-medium text-muted-foreground text-right">Coverage</th>
                <th className="hidden pb-2 text-xs font-medium text-muted-foreground text-right sm:table-cell">
                  Repos
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((team) => (
                <tr
                  key={team.teamId}
                  className="border-b border-border/40"
                  data-testid={`cq-coverage-row-${team.teamId}`}
                >
                  <td className="py-1.5 pr-3 text-xs">{team.teamName}</td>
                  <td className="py-1.5 pr-3 text-right text-xs font-semibold tabular-nums">
                    {team.weightedCoverage === null
                      ? '—'
                      : `${team.weightedCoverage.toFixed(1)}%`}
                  </td>
                  <td className="hidden py-1.5 text-right text-xs text-muted-foreground sm:table-cell">
                    {team.repoCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
